/**
 * Auth0 Provider with navigation integration
 * Configures Auth0 with organization support for multi-tenant architecture
 * Dynamically fetches organization ID based on subdomain
 */
import React, { useState, useEffect } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { BrowserRouter as Router } from 'react-router-dom';
import { getSubdomain } from '../utils/tenant';

const Auth0ProviderWithHistory = ({ children }) => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  const [organizationId, setOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the current subdomain
  const subdomain = getSubdomain();

  // Construct the redirect URI based on current hostname
  const redirectUri = `${window.location.protocol}//${window.location.host}/callback`;

  // Fetch organization ID from subdomain
  useEffect(() => {
    const fetchOrgId = async () => {
      if (!subdomain) {
        // No subdomain - localhost mode
        console.warn('No subdomain detected - running in localhost mode');
        setOrganizationId(null);
        setLoading(false);
        return;
      }

      try {
        // Call public API to get tenant info
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const baseUrl = apiUrl.replace('/api', '');

        const response = await fetch(`${baseUrl}/api/public/tenant-info?subdomain=${subdomain}`);

        if (!response.ok) {
          throw new Error(`Tenant not found for subdomain: ${subdomain}`);
        }

        const data = await response.json();
        setOrganizationId(data.org_id);
        console.log(`✅ Loaded organization: ${data.name} (${data.org_id})`);
      } catch (err) {
        console.error('Failed to fetch organization info:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgId();
  }, [subdomain]);

  const onRedirectCallback = (appState) => {
    // Use window.location for navigation instead of useNavigate
    // This works because Auth0Provider is outside Router context
    window.location.href = appState?.returnTo || window.location.pathname;
  };

  // Show loading state while fetching org ID
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #7c3aed',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <p>Loading tenant information...</p>
      </div>
    );
  }

  // Show error state if tenant not found
  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#e53e3e' }}>
        <h2>Tenant Not Found</h2>
        <p>{error}</p>
        <p style={{ marginTop: '20px', color: '#666' }}>
          Please check the URL and try again, or contact support.
        </p>
      </div>
    );
  }

  // Show error if Auth0 config is missing
  if (!domain || !clientId || !audience) {
    console.error('Auth0 configuration missing. Please check your environment variables.');
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Configuration Error</h2>
        <p>Auth0 configuration is missing. Please check your environment variables:</p>
        <ul style={{ textAlign: 'left', maxWidth: '500px', margin: '20px auto' }}>
          <li>REACT_APP_AUTH0_DOMAIN: {domain || '❌ Missing'}</li>
          <li>REACT_APP_AUTH0_CLIENT_ID: {clientId || '❌ Missing'}</li>
          <li>REACT_APP_AUTH0_AUDIENCE: {audience || '❌ Missing'}</li>
        </ul>
      </div>
    );
  }

  // Build authorization params
  const authorizationParams = {
    redirect_uri: redirectUri,
    audience: audience,
    scope: "openid profile email",
  };

  // Add organization if we have one (only for subdomains)
  if (organizationId) {
    authorizationParams.organization = organizationId;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <Router>
        {children}
      </Router>
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
